/**
 * Human-readable summaries of tool inputs (`thinkingChevron` feature).
 *
 * TypeScript port of `_summarize_tool_input` in AFXTACOAgent
 * (`src/afxtaco_agent/agents/strands_agent.py`). The backend generates these
 * strings for the live WebSocket feed; reloaded history carries the raw
 * `toolUse.input` instead, so we regenerate them here to keep both feeds
 * visually identical.
 *
 * ⚠️ Keep in sync with the Python implementation — if a tool's summary changes
 * there, mirror it here (and vice versa).
 */

/** Max length of a generated detail string, mirroring the backend's cap. */
const MAX_DETAIL_LEN = 120;

/**
 * Produces a brief human-readable summary of what a tool is doing.
 *
 * TypeScript port of `_summarize_tool_input` in AFXTACOAgent
 * (`src/afxtaco_agent/agents/strands_agent.py`). The backend generates this
 * string for the live WebSocket feed; reloaded history carries the raw
 * `toolUse.input` instead, so we regenerate it here to keep both feeds
 * visually identical.
 *
 * ⚠️ Keep in sync with the Python implementation — if a tool's summary changes
 * there, mirror it here (and vice versa).
 *
 * @param toolName - Raw tool name from the agent.
 * @param input - Parsed tool input object.
 * @returns A summary string, or undefined when nothing useful can be extracted
 *   (callers fall back to the tool display name).
 */
export function summarizeToolInput(
  toolName: string,
  input: Record<string, unknown> | undefined,
): string | undefined {
  if (!input || typeof input !== "object") return undefined;

  const str = (key: string): string =>
    typeof input[key] === "string" ? (input[key] as string) : "";

  let summary: string | undefined;

  switch (toolName) {
    case "editor": {
      const command = str("command");
      const path = str("path");
      if (command === "create") {
        summary = path ? `Creating ${path}` : "Creating file";
      } else if (command === "str_replace") {
        summary = path ? `Editing ${path}` : "Editing file";
      } else if (command === "view") {
        summary = path ? `Reading ${path}` : "Viewing file";
      } else if (command && path) {
        summary = `${command} ${path}`;
      } else if (command) {
        summary = command;
      }
      break;
    }
    case "web_search": {
      const query = str("query");
      if (query) summary = `Searching: "${query}"`;
      break;
    }
    case "diagram": {
      const description = str("description");
      if (description) summary = description;
      break;
    }
    case "read_asset": {
      const assetType = str("asset_type");
      if (assetType) summary = `Reading ${assetType}`;
      break;
    }
    case "read_uploaded_file": {
      const path = str("path");
      // Show just the filename, not the full uploaded_files/ path.
      if (path) {
        summary = `Reading uploaded file: ${path.split("/").pop() ?? path}`;
      }
      break;
    }
    case "assign_project_name": {
      // The tool's parameter is `name`; reading a non-existent `project_name`
      // let the generic fallback below surface the project *description*
      // instead of the title.
      const name = str("name");
      if (name) summary = `Setting name: "${name}"`;
      break;
    }
    // AWS Knowledge MCP. The server's parameter name has varied
    // (`search_phrase` vs `query`), so try both rather than falling through to
    // a bare tool name.
    case "search_documentation": {
      const query = str("search_phrase") || str("query");
      if (query) summary = `Searching: "${query}"`;
      break;
    }
    case "read_documentation": {
      const url = str("url") || str("search_phrase") || str("query");
      if (url) summary = url;
      break;
    }
    case "retrieve_skill": {
      const name = str("skill_name");
      if (name) summary = `Reading skill: ${name}`;
      break;
    }
    // drawio cluster. These take structured specs (nodes/edges/operations)
    // rather than a single descriptive string, so summarize their shape —
    // "Drawio Create" alone told the user nothing.
    case "drawio_create": {
      const title = str("title");
      const nodeCount = Array.isArray(input.nodes) ? input.nodes.length : 0;
      if (title && nodeCount) {
        summary = `Creating diagram "${title}" (${nodeCount} ${
          nodeCount === 1 ? "node" : "nodes"
        })`;
      } else if (title) {
        summary = `Creating diagram "${title}"`;
      } else if (nodeCount) {
        summary = `Creating diagram (${nodeCount} ${
          nodeCount === 1 ? "node" : "nodes"
        })`;
      } else {
        summary = "Creating diagram";
      }
      break;
    }
    case "drawio_edit": {
      const ops = Array.isArray(input.operations) ? input.operations : [];
      // Name the operations when they're few and legible; otherwise count them.
      const opNames = ops
        .map((op) =>
          op && typeof op === "object"
            ? String((op as Record<string, unknown>).op ?? "")
            : "",
        )
        .filter(Boolean);
      const unique = Array.from(new Set(opNames));
      if (unique.length > 0 && unique.length <= 3) {
        summary = `Editing diagram: ${unique.join(", ")}`;
      } else if (ops.length) {
        summary = `Editing diagram (${ops.length} ${
          ops.length === 1 ? "operation" : "operations"
        })`;
      } else {
        summary = "Editing diagram";
      }
      break;
    }
    case "drawio_lookup_shape": {
      const names = Array.isArray(input.names)
        ? input.names.filter((n): n is string => typeof n === "string")
        : [];
      if (names.length) summary = `Looking up shapes: ${names.join(", ")}`;
      else summary = "Looking up shapes";
      break;
    }
    case "drawio_read_xml": {
      const path = str("path");
      summary = path ? `Reading diagram: ${path}` : "Reading diagram";
      break;
    }
    case "drawio_write_xml": {
      const path = str("path");
      summary = path ? `Saving diagram: ${path}` : "Saving diagram";
      break;
    }
    case "project_file_list": {
      const directory = str("directory");
      summary = directory ? `Listing ${directory}` : "Listing project files";
      break;
    }
    case "get_aws_icon": {
      const name = str("service_name") || str("name");
      if (name) summary = `Icon: ${name}`;
      break;
    }
    case "list_aws_icons": {
      summary = "Listing AWS icons";
      break;
    }
    default:
      break;
  }

  // Generic fallback: try common field names. `search_phrase` and `title` cover
  // MCP tools and structured-spec tools that aren't special-cased above.
  if (summary === undefined) {
    for (const key of [
      "query",
      "search_phrase",
      "path",
      "url",
      "description",
      "title",
      "name",
    ]) {
      const val = input[key];
      if (val && typeof val === "string") {
        summary = val;
        break;
      }
    }
  }

  if (summary && summary.length > MAX_DETAIL_LEN) {
    summary = summary.slice(0, MAX_DETAIL_LEN - 1) + "…";
  }

  return summary || undefined;
}
